import { Injectable } from '@angular/core';
import { GraphAuthService, GraphInteractionRequiredError } from './graph-auth.service';
import { Client, ResponseType } from '@microsoft/microsoft-graph-client';

export interface DriveItemMeta {
  id: string;
  name: string;
  size?: number;
  parentReference?: {
    driveId?: string;
    id?: string;
    path?: string;
  };
  ['@microsoft.graph.downloadUrl']?: string;
}

@Injectable({ providedIn: 'root' })
export class SharePointPdfGraphService {
  /** Límite típico recomendado para PUT /content en Graph DriveItem (~150MB). */
  private readonly SIMPLE_UPLOAD_MAX_BYTES = 150 * 1024 * 1024;
  private graphClient?: Client;

  constructor(private readonly graphAuth: GraphAuthService) {}

  // ======================================================
  //  Graph Client (auth middleware via MSAL)
  // ======================================================
  private getClient(): Client {
    if (this.graphClient) return this.graphClient;
    this.graphClient = Client.init({
      authProvider: async (done) => {
        try {
          const token = await this.graphAuth.getGraphAccessTokenSilentOnly();
          done(null, token);
        } catch (e: any) {
          done(e, null);
        }
      },
    });
    return this.graphClient;
  }

  // ======================================================
  //  Auth helpers (Redirect-only)
  // ======================================================
  async loginMicrosoftRedirect(): Promise<void> {
    this.graphAuth.loginRedirect();
  }

  // ======================================================
  //  Metadata
  // ======================================================
  async getDriveItemMeta(driveId: string, driveItemId: string): Promise<DriveItemMeta> {
    const client = this.getClient();
    const path = `/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(driveItemId)}`;
    // Nota: @microsoft.graph.downloadUrl no se puede seleccionar siempre; pero lo pedimos igual.
    return await client
      .api(path)
      .query({ $select: 'id,name,size,parentReference,@microsoft.graph.downloadUrl' })
      .get();
  }

  // ======================================================
  //  Download
  // ======================================================
  async downloadPdfBlob(
    driveId: string,
    driveItemId: string
  ): Promise<{ blob: Blob; fileName: string; meta: DriveItemMeta }> {
    const client = this.getClient();
    const meta = await this.getDriveItemMeta(driveId, driveItemId);
    const fileName = meta.name;
    // Opción 1: usar downloadUrl si existe (faster, directo a SharePoint)
    // Nota: puede devolver 401/403 si expiró o cambió el acceso -> tratamos como "interaction required"
    const downloadUrl = meta['@microsoft.graph.downloadUrl'];
    if (downloadUrl) {
      const res = await fetch(downloadUrl);
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          throw new GraphInteractionRequiredError();
        }
        throw new Error(`No se pudo descargar PDF. HTTP ${res.status}`);
      }
      const blob = await res.blob();
      return { blob, fileName, meta };
    }

    // Opción 2: /content vía Graph client (autenticado)
    const contentPath = `/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(driveItemId)}/content`;
    try {
      const blob = (await client.api(contentPath).responseType(ResponseType.BLOB).get()) as Blob;
      return { blob, fileName, meta };
    } catch (e: any) {
      const status = Number(e?.statusCode ?? e?.status ?? 0);
      const code = String(e?.code ?? e?.error?.code ?? '').toLowerCase();
      if (status === 401 || status === 403 || code.includes('invalidauthenticationtoken')) {
        throw new GraphInteractionRequiredError();
      }
      throw e;
    }
  }

  // ======================================================
  //  Name builder
  // ======================================================
  buildTargetFileName(originalName: string, suffix?: string): string {
    const dot = originalName.lastIndexOf('.');
    const base = dot > 0 ? originalName.slice(0, dot) : originalName;
    const ext = dot > 0 ? originalName.slice(dot) : '.pdf';
    const safeSuffix = (suffix && suffix.trim().length > 0) ? suffix.trim() : `_${this.timestamp()}`;
    return `${base}${safeSuffix}${ext || '.pdf'}`;
  }

  // ======================================================
  //  Upload (NEW + REWRITE)
  // ======================================================
  async uploadPdfBlob(
    driveId: string,
    originalItemId: string,
    pdfBlob: Blob,
    mode: 'new' | 'rewrite',
    targetNameIfNew?: string
  ): Promise<any> {
    const client = this.getClient();
    if (mode === 'rewrite') {
      return this.uploadOverwriteItemById(client, driveId, originalItemId, pdfBlob);
    }
    if (!targetNameIfNew) throw new Error('targetNameIfNew requerido para mode=new');
    return this.uploadAsNewInSameFolder(client, driveId, originalItemId, targetNameIfNew, pdfBlob);
  }

  // ======================================================
  //  Implementación "NEW"
  // ======================================================
  private async uploadAsNewInSameFolder(
    client: Client,
    driveId: string,
    originalItemId: string,
    newFileName: string,
    blob: Blob
  ): Promise<any> {
    const meta = await this.getDriveItemMeta(driveId, originalItemId);
    const parentId = meta.parentReference?.id;
    if (!parentId) throw new Error('No se pudo resolver parentReference.id del item.');

    if (blob.size <= this.SIMPLE_UPLOAD_MAX_BYTES) {
      try {
        return await this.simpleUploadNewFile(client, driveId, parentId, newFileName, blob);
      } catch (e: any) {
        if (!this.shouldFallbackToSession(e)) throw e;
      }
    }
    return this.uploadNewFileViaSession(client, driveId, parentId, newFileName, blob);
  }

  private async simpleUploadNewFile(
    client: Client,
    driveId: string,
    parentId: string,
    fileName: string,
    blob: Blob
  ): Promise<any> {
    const path =
      `/drives/${encodeURIComponent(driveId)}` +
      `/items/${encodeURIComponent(parentId)}:/${encodeURIComponent(fileName)}:/content`;
    // PUT /content devuelve el DriveItem resultante
    return await client.api(path).put(blob);
  }

  private async uploadNewFileViaSession(
    client: Client,
    driveId: string,
    parentId: string,
    fileName: string,
    blob: Blob
  ): Promise<any> {
    const createSessionPath =
      `/drives/${encodeURIComponent(driveId)}` +
      `/items/${encodeURIComponent(parentId)}:/${encodeURIComponent(fileName)}:/createUploadSession`;
    const body = {
      item: {
        '@microsoft.graph.conflictBehavior': 'rename',
        name: fileName,
      },
    };
    const session = await client.api(createSessionPath).post(body);
    return this.uploadByChunks(session.uploadUrl, blob);
  }

  // ======================================================
  //  Implementación "REWRITE"
  // ======================================================
  private async uploadOverwriteItemById(
    client: Client,
    driveId: string,
    itemId: string,
    blob: Blob
  ): Promise<any> {
    if (blob.size <= this.SIMPLE_UPLOAD_MAX_BYTES) {
      try {
        const path =
          `/drives/${encodeURIComponent(driveId)}` + `/items/${encodeURIComponent(itemId)}/content`;
        return await client.api(path).put(blob);
      } catch (e: any) {
        if (!this.shouldFallbackToSession(e)) throw e;
      }
    }
    const createSessionPath =
      `/drives/${encodeURIComponent(driveId)}` + `/items/${encodeURIComponent(itemId)}/createUploadSession`;
    const body = { item: { '@microsoft.graph.conflictBehavior': 'replace' } };
    const session = await client.api(createSessionPath).post(body);
    return this.uploadByChunks(session.uploadUrl, blob);
  }

  // ======================================================
  //  Upload Session (Chunks) - uploadUrl es a SharePoint, no Graph
  // ======================================================
  private async uploadByChunks(uploadUrl: string, blob: Blob): Promise<any> {
    const chunkSize = 10 * 1024 * 1024;
    const total = blob.size;
    let start = 0;
    let lastResponse: any = null;
    while (start < total) {
      const end = Math.min(start + chunkSize, total);
      const chunk = blob.slice(start, end);
      const contentRange = `bytes ${start}-${end - 1}/${total}`;
      const res = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Length': String(end - start),
          'Content-Range': contentRange,
        },
        body: chunk,
      });
      if (!res.ok && res.status !== 202 && res.status !== 201 && res.status !== 200) {
        throw new Error(`Chunk upload falló. HTTP ${res.status}`);
      }
      // Graph devuelve JSON con nextExpectedRanges o el DriveItem final
      try {
        lastResponse = await res.json();
      } catch {
        lastResponse = null;
      }
      start = end;
    }
    return lastResponse;
  }

  // ======================================================
  //  Fallback rules
  // ======================================================
  private shouldFallbackToSession(e: any): boolean {
    const status = Number(e?.status ?? e?.statusCode ?? 0);
    if (status === 413 || status === 408 || status === 429) return true;
    if (status >= 500 && status < 600) return true;
    if (status === 0) return true;
    return false;
  }

  // ======================================================
  //  Utils
  // ======================================================
  private timestamp(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}${pad(d.getHours())}${pad(
      d.getMinutes()
    )}${pad(d.getSeconds())}`;
  }
}
